// Auth Service for Cognito-based authentication
// PERMANENT AUTH URLS - ALWAYS USE AWS, NEVER LOCALHOST BACKEND
import { getAuthApiBaseUrl as getPermanentAuthApiBaseUrl, getApiBaseUrl as getPermanentApiBaseUrl } from '../config/permanent-urls';

const AUTH_API_BASE_URL = getPermanentAuthApiBaseUrl();

export interface AuthUser {
  userId: string;
  email: string;
  status: string;
  createdAt: string;
  lastModified: string;
}

export interface AuthResponse {
  success: boolean;
  data?: any;
  error?: {
    code: string;
    message: string;
    userFriendly: boolean;
  };
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = AUTH_API_BASE_URL;
  }

  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Create error with user-friendly message
        const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText || 'Unknown error'}`;
        const error = new Error(errorMessage);
        (error as any).errorCode = errorData.error?.code;
        (error as any).userFriendly = errorData.error?.userFriendly || false;
        (error as any).status = response.status;
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    return response;
  }

  async getUser(email: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>(`/user/${encodeURIComponent(email)}`, {
      method: 'GET',
    });
    
    return response;
  }

  // Helper method to check if user exists
  async userExists(email: string): Promise<boolean> {
    try {
      const response = await this.getUser(email);
      return response.success && !!response.data;
    } catch (error) {
      // If error is 404, user doesn't exist
      if (error instanceof Error && error.message.includes('404')) {
        return false;
      }
      // For other errors, re-throw
      throw error;
    }
  }

  async login(email: string, password: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await this.request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      // Handle specific error cases
      if (error instanceof Error) {
        const status = (error as any).status;
        if (status === 404) {
          return {
            success: false,
            error: 'User not found. Please check your email or register for a new account.'
          };
        } else if (status === 401) {
          return {
            success: false,
            error: 'Invalid credentials. Please check your password.'
          };
        }
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }

  // Helper method to validate email format
  isValidEmail(email: string): boolean {
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(email);
  }

  // Helper method to validate password strength
  validatePassword(password: string): { isValid: boolean; message?: string } {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long.' };
    }
    
    if (!/[A-Z]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter.' };
    }
    
    if (!/[a-z]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter.' };
    }
    
    if (!/[0-9]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one number.' };
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return { isValid: false, message: 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>).' };
    }
    
    return { isValid: true };
  }

  async resendVerification(email: string): Promise<AuthResponse> {
    // Use main API Gateway (same as verifyEmail) - auth endpoints are on main API Gateway
    const mainApiUrl = getPermanentApiBaseUrl();
    const response = await fetch(`${mainApiUrl}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to resend verification code');
    }
    
    const result = await response.json();
    return result;
  }
}

export const authService = new AuthService();
